/**
 * Meet-style Document PiP — video tiles + full control strip.
 * While screen sharing, also shows a live preview of the shared screen.
 */

import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  isTrackReference,
  useTracks,
  VideoTrack as VideoTrackPrimitive,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import {
  MessageSquare,
  Mic,
  MicOff,
  MonitorUp,
  PictureInPicture2,
  Smile,
  Video,
  VideoOff,
} from "lucide-react";
import { LiveKitVideoTiles } from "./LiveKitVideoTiles";
import { LIVE_REACTION_EMOJIS } from "./liveReactionTypes";
import type { DocumentPipWindow } from "./documentPictureInPicture";

// CRA + TS 4.9 reject LiveKit's ReactNode return type for JSX components.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const VideoTrack = VideoTrackPrimitive as any;

function PipScreenSharePreview() {
  const tracks = useTracks(
    [{ source: Track.Source.ScreenShare, withPlaceholder: false }],
    { onlySubscribed: false },
  );
  const screenShare = useMemo(() => {
    for (const candidate of tracks) {
      if (
        isTrackReference(candidate) &&
        candidate.source === Track.Source.ScreenShare &&
        !candidate.publication?.isMuted
      ) {
        return candidate;
      }
    }
    return undefined;
  }, [tracks]);

  if (!screenShare) {
    return (
      <div
        style={{
          flex: 1.4,
          minHeight: 120,
          borderRadius: 10,
          background: "#0b0d12",
          border: "1px solid rgba(255,255,255,0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#64748b",
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        Starting screen preview…
      </div>
    );
  }

  const label = `${
    screenShare.participant.name || screenShare.participant.identity || "You"
  } · Shared screen`;

  return (
    <div
      style={{
        position: "relative",
        flex: 1.4,
        minHeight: 120,
        borderRadius: 10,
        overflow: "hidden",
        background: "#0b0d12",
        border: "1px solid rgba(96,165,250,0.35)",
      }}
    >
      <VideoTrack
        trackRef={screenShare}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          background: "#0b0d12",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: 8,
          bottom: 8,
          padding: "4px 8px",
          borderRadius: 6,
          background: "rgba(0,0,0,0.7)",
          color: "#fff",
          fontSize: 11,
          fontWeight: 600,
          pointerEvents: "none",
        }}
      >
        {label}
      </div>
    </div>
  );
}

export function LiveSessionPipBanner({
  onBringBack,
  reason = "share",
}: {
  onBringBack: () => void;
  reason?: "share" | "manual";
}) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 30,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        padding: 24,
        background: "#1a1d26",
        textAlign: "center",
      }}
    >
      <PictureInPicture2 size={36} color="#94a3b8" />
      <p
        style={{
          margin: 0,
          maxWidth: 420,
          fontSize: 16,
          fontWeight: 600,
          color: "#f1f5f9",
          lineHeight: 1.45,
        }}
      >
        {reason === "share"
          ? "Picture-in-picture is open because you're screen sharing. Using picture-in-picture lets you see others in the call while you screen share."
          : "Picture-in-picture is open. You can keep watching the session while you use other apps or tabs."}
      </p>
      <button
        type="button"
        onClick={onBringBack}
        style={{
          border: "none",
          background: "transparent",
          color: "#60a5fa",
          fontSize: 14,
          fontWeight: 700,
          cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        Bring the call back here
      </button>
    </div>
  );
}

function PipBtn({
  icon,
  label,
  onClick,
  active,
  danger,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        minWidth: 52,
        border: "none",
        borderRadius: 10,
        padding: "6px 4px",
        background: active
          ? "rgba(255,255,255,0.12)"
          : danger
            ? "rgba(220,38,38,0.28)"
            : "transparent",
        color: danger ? "#fca5a5" : active ? "#fff" : "#cbd5e1",
        fontSize: 9,
        fontWeight: 700,
        cursor: "pointer",
        lineHeight: 1.1,
      }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

export function LiveSessionPipPortal({
  pipWindow,
  isTrainer,
  selfName,
  isMicrophoneEnabled,
  isCameraEnabled,
  isScreenShareEnabled,
  canScreenShare = true,
  onToggleMicrophone,
  onToggleCamera,
  onToggleScreenShare,
  onChat,
  onReaction,
  onExitPip,
}: {
  pipWindow: DocumentPipWindow;
  isTrainer: boolean;
  selfName: string;
  isMicrophoneEnabled: boolean;
  isCameraEnabled: boolean;
  isScreenShareEnabled: boolean;
  canScreenShare?: boolean;
  onToggleMicrophone: () => void;
  onToggleCamera: () => void;
  onToggleScreenShare: () => void;
  onChat: () => void;
  onReaction: (emoji: string) => void;
  onExitPip: () => void;
}) {
  const [reactionsOpen, setReactionsOpen] = useState(false);
  const root = pipWindow.document.getElementById("live-session-pip-root");
  if (!root) return null;

  return createPortal(
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        background: "#0b0f17",
      }}
    >
      <div
        style={{
          flexShrink: 0,
          padding: "8px 10px",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          fontSize: 11,
          fontWeight: 700,
          color: "#94a3b8",
        }}
      >
        Live session · Picture-in-picture
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          padding: 8,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {isScreenShareEnabled ? <PipScreenSharePreview /> : null}
        <div
          style={{
            flex: isScreenShareEnabled ? 1 : 1,
            minHeight: isScreenShareEnabled ? 96 : 0,
            overflow: "hidden",
          }}
        >
          <LiveKitVideoTiles
            isTrainer={isTrainer}
            selfName={selfName}
            horizontal={isScreenShareEnabled}
          />
        </div>
      </div>

      {reactionsOpen ? (
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            justifyContent: "center",
            padding: "6px 8px",
            borderTop: "1px solid rgba(255,255,255,0.08)",
            background: "#141820",
          }}
        >
          {LIVE_REACTION_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => {
                onReaction(emoji);
                setReactionsOpen(false);
              }}
              style={{
                border: "none",
                background: "transparent",
                fontSize: 18,
                cursor: "pointer",
                padding: 4,
                borderRadius: 8,
              }}
            >
              {emoji}
            </button>
          ))}
        </div>
      ) : null}

      <div
        style={{
          flexShrink: 0,
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          justifyContent: "center",
          alignItems: "center",
          padding: "6px 6px 10px",
          borderTop: "1px solid rgba(255,255,255,0.08)",
          background: "#141820",
        }}
      >
        <PipBtn
          icon={
            isMicrophoneEnabled ? <Mic size={16} /> : <MicOff size={16} />
          }
          label={isMicrophoneEnabled ? "Mute" : "Unmute"}
          danger={!isMicrophoneEnabled}
          onClick={onToggleMicrophone}
        />
        <PipBtn
          icon={isCameraEnabled ? <Video size={16} /> : <VideoOff size={16} />}
          label={isCameraEnabled ? "Stop Video" : "Start Video"}
          danger={!isCameraEnabled}
          onClick={onToggleCamera}
        />
        <PipBtn
          icon={<MessageSquare size={16} />}
          label="Chat"
          onClick={onChat}
        />
        {canScreenShare ? (
          <PipBtn
            icon={<MonitorUp size={16} />}
            label={isScreenShareEnabled ? "Stop Share" : "Share"}
            active={isScreenShareEnabled}
            onClick={onToggleScreenShare}
          />
        ) : null}
        <PipBtn
          icon={<PictureInPicture2 size={16} />}
          label="Exit PiP"
          active
          onClick={onExitPip}
        />
        <PipBtn
          icon={<Smile size={16} />}
          label="Reactions"
          active={reactionsOpen}
          onClick={() => setReactionsOpen((v) => !v)}
        />
      </div>
    </div>,
    root,
  );
}
